import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { Upload } from 'lucide-react';

export default function FloatingUploadButton3D({ onClick, position = 'bottom-right' }) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const sphereRef = useRef(null);
  const particlesRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    camera.position.z = 3;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: true 
    });
    renderer.setSize(80, 80);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Main sphere (glowing)
    const sphereGeometry = new THREE.SphereGeometry(1, 32, 32);
    const sphereMaterial = new THREE.MeshPhongMaterial({
      color: 0x3b82f6,
      emissive: 0x1e40af,
      emissiveIntensity: 0.5,
      shininess: 100,
      transparent: true,
      opacity: 0.9
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    scene.add(sphere);
    sphereRef.current = sphere;

    // Outer glow ring
    const ringGeometry = new THREE.TorusGeometry(1.3, 0.05, 16, 100);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0x60a5fa,
      transparent: true,
      opacity: 0.6
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = Math.PI / 2;
    scene.add(ring);

    // Particles
    const particlesGeometry = new THREE.BufferGeometry();
    const particlesCount = 100;
    const positions = new Float32Array(particlesCount * 3);
    
    for (let i = 0; i < particlesCount * 3; i++) {
      positions[i] = (Math.random() - 0.5) * 5;
    }
    
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const particlesMaterial = new THREE.PointsMaterial({
      color: 0x60a5fa,
      size: 0.05,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending
    });
    
    const particles = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particles);
    particlesRef.current = particles;

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0x3b82f6, 1, 100);
    pointLight.position.set(0, 0, 5);
    scene.add(pointLight);

    // Animation
    let animationId;
    const animate = () => {
      animationId = requestAnimationFrame(animate);

      // Rotate sphere
      sphere.rotation.y += 0.01;
      sphere.rotation.x += 0.005;

      // Rotate ring
      ring.rotation.z += 0.02;

      // Pulse effect
      const pulse = Math.sin(Date.now() * 0.003) * 0.1 + 1;
      sphere.scale.set(pulse, pulse, pulse);

      // Particles rotation
      particles.rotation.y += 0.001;

      // Hover effect
      if (isHovered) {
        sphereMaterial.emissiveIntensity = 0.8;
        ringMaterial.opacity = 0.9;
        particlesMaterial.opacity = 0.9;
      } else {
        sphereMaterial.emissiveIntensity = 0.5;
        ringMaterial.opacity = 0.6;
        particlesMaterial.opacity = 0.6;
      }

      // Click effect
      if (isClicked) {
        sphere.scale.set(0.8, 0.8, 0.8);
        setTimeout(() => setIsClicked(false), 200);
      }

      renderer.render(scene, camera);
    };

    animate();

    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      renderer.dispose();
      sphereGeometry.dispose();
      sphereMaterial.dispose();
      ringGeometry.dispose();
      ringMaterial.dispose();
      particlesGeometry.dispose();
      particlesMaterial.dispose();
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, [isHovered, isClicked]);

  const handleClick = () => {
    setIsClicked(true);
    if (onClick) onClick();
  };

  const positionClasses = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-24 left-6',
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6'
  };

  return (
    <div 
      className={`fixed ${positionClasses[position]} z-50 group cursor-pointer`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      {/* 3D Canvas */}
      <div 
        ref={containerRef} 
        className="relative w-20 h-20"
      />
      
      {/* Icon Overlay */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <Upload 
          className={`w-8 h-8 text-white transition-all duration-300 ${
            isHovered ? 'scale-110' : 'scale-100'
          }`}
          strokeWidth={2.5}
        />
      </div>

      {/* Tooltip */}
      <div className={`
        absolute bottom-full mb-2 right-0
        px-3 py-2 bg-gray-900 text-white text-sm rounded-lg
        whitespace-nowrap opacity-0 group-hover:opacity-100
        transition-opacity duration-300 pointer-events-none
        shadow-lg
      `}>
        Dokument hochladen
        <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
      </div>

      {/* Ripple effect on click */}
      {isClicked && (
        <div className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-75" />
      )}
    </div>
  );
}

